import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from './lib/axiosClient';
import { useTenant } from './contexts/TenantContext';

const TAX_RATE = 0.0825;
const TIP_PRESETS = [15, 18, 20, 25];
const PAYMENT_METHODS = [
  { key: 'gift_card', label: 'Gift Card', icon: '🎁' },
  { key: 'cash', icon: '💵', label: 'Cash' },
  { key: 'card', icon: '💳', label: 'Card' },
  { key: 'venmo', icon: '📱', label: 'Venmo' },
  { key: 'zelle', icon: '⚡', label: 'Zelle' },
  { key: 'check', icon: '📝', label: 'Check' },
  { key: 'other', icon: '🔄', label: 'Other' },
];

function Checkout() {
  const { tenant } = useTenant();
  const isSpaCheckout = ['spa','massage','salon','barbershop','barber','nails','nail_salon','hair_salon','beauty','medspa','med_spa'].includes((tenant?.business_type||'').toLowerCase());
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cart
  const [items, setItems] = useState([]); // { id, type:'service'|'product', name, price, quantity, product_id? }
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [bookingId, setBookingId] = useState(null);

  // Payment
  const [tipMode, setTipMode] = useState('preset'); // preset or custom
  const [tipPreset, setTipPreset] = useState(null);
  const [tipCustom, setTipCustom] = useState('');
  const [discountMode, setDiscountMode] = useState('$'); // $ or %
  const [discountValue, setDiscountValue] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardBalance, setGiftCardBalance] = useState(null);
  const [giftCardApplied, setGiftCardApplied] = useState(0);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  // UI
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCust, setNewCust] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [addingCust, setAddingCust] = useState(false);
  const [itemTab, setItemTab] = useState('services'); // services, products, retail
  const [itemSearch, setItemSearch] = useState('');
  const [receipt, setReceipt] = useState(null); // receipt data after payment
  const [showRebook, setShowRebook] = useState(false);
  const [rebookDate, setRebookDate] = useState('');
  const [rebookTime, setRebookTime] = useState('10:00');
  const [rebookService, setRebookService] = useState('');
  const [rebookSaving, setRebookSaving] = useState(false);
  const custSearchRef = useRef(null);
  const [customerBookings, setCustomerBookings] = useState([]);

  // Fetch customer's upcoming bookings when selected
  const selectCustomerAndFetch = async (c) => {
    setSelectedCustomer(c);
    setCustomerSearch(c.first_name + ' ' + (c.last_name || ''));
    setShowCustomerDropdown(false);
    setCustomerBookings([]);
    if (!c || c.id === 'walk-in') return;
    try {
      var res = await axios.get('/api/bookings');
      var all = res.data.bookings || [];
      var today = new Date().toISOString().slice(0, 10);
      var upcoming = all.filter(b =>
        b.contact_id === c.id &&
        (b.booking_date || '').slice(0, 10) >= today &&
        ['pending', 'confirmed', 'checked_in'].includes(b.status)
      );
      setCustomerBookings(upcoming);
    } catch (e) { console.error('Fetch bookings error:', e); }
  };

  useEffect(() => { loadData(); checkBookingPrefill(); }, []);

  const loadData = async () => {
    try {
      const [cr, sr, pr, str] = await Promise.all([
        axios.get('/api/customers'),
        axios.get('/api/services?active_only=true'),
        axios.get('/api/inventory').catch(() => ({ data: { products: [] } })),
        axios.get('/api/staff'),
      ]);
      setCustomers((cr.data.customers || cr.data.contacts || []).sort((a, b) =>
        ((a.first_name || '') + ' ' + (a.last_name || '')).localeCompare((b.first_name || '') + ' ' + (b.last_name || ''))
      ));
      setServices(sr.data.services || []);
      setProducts((pr.data.products || []).filter(p => p.is_active !== false));
      setStaffList(sr.data.staff || str.data.staff || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const checkBookingPrefill = async () => {
    var params = new URLSearchParams(window.location.search);
    var bid = params.get('booking_id') || params.get('booking');
    if (!bid) return;
    try {
      var res = await axios.get('/api/bookings/' + bid);
      var b = res.data.booking || res.data;
      if (b && b.id) {
        setBookingId(b.id);
        if (b.contact_id) {
          var cr = await axios.get('/api/customers');
          var cust = (cr.data.customers || cr.data.contacts || []).find(c => c.id === b.contact_id);
          if (cust) { selectCustomerAndFetch(cust); }
        }
        if (b.staff_id) setSelectedStaff(String(b.staff_id));
        // Add the booking service
        var svc = b.services || b.service;
        if (svc) {
          setItems([{ id: 'svc-' + (svc.id || b.service_id), type: 'service', name: svc.name || 'Service', price: parseFloat(svc.price || b.final_price || 0), quantity: 1 }]);
        } else if (b.final_price) {
          setItems([{ id: 'svc-booking', type: 'service', name: 'Booked Service', price: parseFloat(b.final_price), quantity: 1 }]);
        }
        if (b.notes) setNotes(b.notes);
      }
    } catch (e) { console.error('Booking prefill error:', e); }
  };

  // ── Calculations ──
  const subtotal = items.reduce((s, i) => s + (i.price * i.quantity), 0);
  const taxAmount = subtotal * TAX_RATE;
  const tipAmount = tipMode === 'preset' && tipPreset ? subtotal * (tipPreset / 100) : parseFloat(tipCustom) || 0;
  const discountAmount = discountMode === '%'
    ? subtotal * ((parseFloat(discountValue) || 0) / 100)
    : parseFloat(discountValue) || 0;
  const total = Math.max(0, subtotal + taxAmount + tipAmount - discountAmount);

  // ── Smart Customer Search ──
  const [highlightIdx, setHighlightIdx] = useState(-1);

  const filteredCustomers = useMemo(() => {
    var q = (customerSearch || '').trim().toLowerCase();
    var results;
    if (!q) {
      // No search — show recent/frequent first (by most recent booking or last_visit)
      results = [...customers].sort((a, b) => {
        var aDate = a.last_visit || a.updated_at || a.created_at || '';
        var bDate = b.last_visit || b.updated_at || b.created_at || '';
        return bDate.localeCompare(aDate);
      }).slice(0, 15);
    } else {
      // Score each customer by match quality
      results = customers.map(c => {
        var fullName = ((c.first_name || '') + ' ' + (c.last_name || '')).toLowerCase();
        var phone = (c.phone || '').replace(/\D/g, '');
        var email = (c.email || '').toLowerCase();
        var score = 0;

        // Exact first name start = highest
        if ((c.first_name || '').toLowerCase().startsWith(q)) score += 100;
        else if (fullName.startsWith(q)) score += 90;
        else if ((c.first_name || '').toLowerCase().includes(q)) score += 70;
        else if ((c.last_name || '').toLowerCase().startsWith(q)) score += 80;
        else if ((c.last_name || '').toLowerCase().includes(q)) score += 60;
        // Phone match
        else if (phone.includes(q.replace(/\D/g, '')) && q.length >= 3) score += 85;
        // Email match
        else if (email.includes(q)) score += 50;
        else return null;

        return { ...c, _score: score };
      }).filter(Boolean).sort((a, b) => b._score - a._score).slice(0, 10);
    }
    setHighlightIdx(-1);
    return results;
  }, [customers, customerSearch]);

  const handleCustKeyDown = (e) => {
    if (!showCustomerDropdown) return;
    var max = filteredCustomers.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(prev => Math.min(prev + 1, max - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(prev => Math.max(prev - 1, 0)); }
    else if (e.key === 'Enter' && highlightIdx >= 0 && filteredCustomers[highlightIdx]) {
      e.preventDefault();
      selectCustomerAndFetch(filteredCustomers[highlightIdx]);
    }
    else if (e.key === 'Escape') { setShowCustomerDropdown(false); }
  };

  // ── Item Search ──
  const filteredItems = useMemo(() => {
    var q = itemSearch.toLowerCase();
    if (itemTab === 'services') {
      return services.filter(s => !q || s.name.toLowerCase().includes(q) || (s.category || '').toLowerCase().includes(q));
    } else {
      return products.filter(p => !q || p.name.toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q));
    }
  }, [services, products, itemTab, itemSearch]);

  // ── Actions ──
  const addItem = (item, type) => {
    var key = type + '-' + item.id;
    var existing = items.find(i => i.id === key);
    if (existing) {
      setItems(items.map(i => i.id === key ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setItems([...items, {
        id: key, type, name: item.name,
        price: parseFloat(type === 'product' ? item.retail_price : item.price) || 0,
        quantity: 1,
        product_id: type === 'product' ? item.id : null,
        service_id: type === 'service' ? item.id : null,
        duration: type === 'service' ? item.duration : null,
      }]);
    }
  };

  const removeItem = (id) => setItems(items.filter(i => i.id !== id));
  const updateQty = (id, qty) => setItems(items.map(i => i.id === id ? { ...i, quantity: Math.max(1, qty) } : i));

  const handleAddCustomer = async () => {
    if (!newCust.first_name.trim() || !newCust.phone.trim()) return alert('Name and phone required');
    setAddingCust(true);
    try {
      var res = await axios.post('/api/customers', newCust);
      if (res.data.success) {
        var c = res.data.customer;
        setCustomers(prev => [...prev, c].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || '')));
        selectCustomerAndFetch(c);
        setShowNewCustomer(false);
        setNewCust({ first_name: '', last_name: '', phone: '', email: '' });
      }
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setAddingCust(false);
  };

  const handleRebook = async () => {
    if (!rebookDate || !receipt?.customer) return alert('Select a date');
    setRebookSaving(true);
    try {
      await axios.post('/api/bookings', {
        contact_id: receipt.customer.id,
        service_name: rebookService || (receipt.items[0]?.name || 'Appointment'),
        start_time: rebookDate + 'T' + rebookTime + ':00',
        staff_id: null,
        notes: 'Rebooked from checkout',
        status: 'confirmed',
      });
      setShowRebook(false);
      alert('✅ Next appointment booked for ' + receipt.customer.first_name + '!');
    } catch(e) { alert('Error booking: ' + (e.response?.data?.error || e.message)); }
    setRebookSaving(false);
  };

  const handleCheckout = async () => {
    if (items.length === 0) return alert('Add at least one item');
    if (!selectedCustomer) return alert('Select a customer');
    setProcessing(true);
    try {
      var lineItems = items.map(i => ({
        type: i.type, name: i.name, price: i.price, quantity: i.quantity,
        product_id: i.product_id || null, service_id: i.service_id || null,
      }));
      var res = await axios.post('/api/transactions', {
        contact_id: selectedCustomer.id,
        booking_id: bookingId || null,
        staff_id: selectedStaff ? parseInt(selectedStaff) : null,
        subtotal: subtotal,
        tax_amount: taxAmount,
        tip_amount: tipAmount,
        discount_amount: discountAmount,
        payment_method: paymentMethod,
        notes: notes,
        line_items: lineItems,
      });
      if (res.data.success) {
        var staffName = '';
        if (selectedStaff) {
          var sf = staffList.find(s => String(s.id) === selectedStaff);
          if (sf) staffName = sf.first_name + ' ' + (sf.last_name || '');
        }
        setReceipt({
          id: res.data.transaction?.id,
          date: new Date(),
          customer: selectedCustomer,
          staff: staffName,
          items: items,
          subtotal, taxAmount, tipAmount, discountAmount, total,
          paymentMethod,
          notes,
        });
      }
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
    setProcessing(false);
  };

  const resetCheckout = () => {
    setItems([]); setSelectedCustomer(null); setCustomerSearch('');
    setSelectedStaff(''); setTipPreset(null); setTipCustom('');
    setDiscountValue(''); setPaymentMethod('cash'); setNotes('');
    setBookingId(null); setReceipt(null); setCustomerBookings([]);
    window.history.replaceState({}, '', window.location.pathname);
  };

  const printReceipt = () => {
    var w = window.open('', '_blank', 'width=400,height=600');
    if (!w) return;
    var r = receipt;
    var html = `<html><head><style>
      body{font-family:-apple-system,sans-serif;padding:20px;max-width:350px;margin:0 auto;font-size:13px;color:#222}
      h1{text-align:center;font-size:18px;margin:0 0 4px}
      .sub{text-align:center;color:#888;font-size:11px;margin-bottom:16px}
      hr{border:none;border-top:1px dashed #ccc;margin:12px 0}
      .row{display:flex;justify-content:space-between;padding:3px 0}
      .bold{font-weight:700}
      .big{font-size:18px}
      .right{text-align:right}
      .item{padding:4px 0}
      .item .name{font-weight:600}
      .item .detail{color:#888;font-size:11px}
      .center{text-align:center}
      .footer{margin-top:16px;text-align:center;color:#888;font-size:10px}
      @media print{body{padding:10px}}
    </style></head><body>`;
    html += `<h1>Receipt</h1>`;
    html += `<div class="sub">${r.date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} ${r.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>`;
    html += `<hr>`;
    html += `<div class="row"><span>Customer:</span><span class="bold">${r.customer.first_name} ${r.customer.last_name || ''}</span></div>`;
    if (r.staff) html += `<div class="row"><span>Staff:</span><span>${r.staff}</span></div>`;
    html += `<div class="row"><span>Payment:</span><span>${r.paymentMethod.toUpperCase()}</span></div>`;
    if (r.id) html += `<div class="row"><span>Txn #:</span><span>${r.id}</span></div>`;
    html += `<hr>`;
    r.items.forEach(i => {
      html += `<div class="item"><div class="row"><span class="name">${i.name}${i.quantity > 1 ? ' ×' + i.quantity : ''}</span><span>$${(i.price * i.quantity).toFixed(2)}</span></div>`;
      if (i.type === 'product') html += `<div class="detail">Product</div>`;
      if (i.duration) html += `<div class="detail">${i.duration} min</div>`;
      html += `</div>`;
    });
    html += `<hr>`;
    html += `<div class="row"><span>Subtotal</span><span>$${r.subtotal.toFixed(2)}</span></div>`;
    html += `<div class="row"><span>Tax (8.25%)</span><span>$${r.taxAmount.toFixed(2)}</span></div>`;
    if (r.tipAmount > 0) html += `<div class="row"><span>Tip</span><span>$${r.tipAmount.toFixed(2)}</span></div>`;
    if (r.discountAmount > 0) html += `<div class="row"><span>Discount</span><span>-$${r.discountAmount.toFixed(2)}</span></div>`;
    html += `<hr>`;
    html += `<div class="row bold big"><span>Total</span><span>$${r.total.toFixed(2)}</span></div>`;
    if (r.notes) html += `<hr><div class="center" style="font-size:11px;color:#888">Note: ${r.notes}</div>`;
    html += `<div class="footer"><p>Thank you for your business!</p></div>`;
    html += `</body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  // ── Styles ──
  const S = {
    input: { width: '100%', padding: '12px 16px', background: '#1a1c2e', border: '1px solid #1e1e35', borderRadius: '10px', color: '#e2e2e8', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    label: { fontSize: '13px', fontWeight: '600', color: '#888', marginBottom: '6px', display: 'block' },
    card: { background: '#12121f', border: '1px solid #1e1e35', borderRadius: '16px', padding: '20px', marginBottom: '14px' },
    heading: { color: '#fff', fontSize: '15px', fontWeight: '700', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px' },
    btn: (active) => ({
      padding: '10px 16px', borderRadius: '10px', border: active ? '2px solid #ff6b2b' : '2px solid #1e1e35',
      background: active ? '#ff6b2b15' : '#1a1c2e', color: active ? '#ff6b2b' : '#888',
      fontSize: '13px', fontWeight: '700', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
    }),
    pill: (active) => ({ padding: '7px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: active ? '#ff6b2b' : 'transparent', color: active ? '#fff' : '#888' }),
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#888' }}><p>Loading checkout...</p></div>;

  // ════════════════════════════════════════════
  // ── RECEIPT SCREEN ──
  // ════════════════════════════════════════════
  if (receipt) {
    var r = receipt;
    return (
      <div style={{ maxWidth: '520px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', padding: '32px 0 24px' }}>
          <div style={{ fontSize: '56px', marginBottom: '12px' }}>✅</div>
          <h1 style={{ color: '#00b894', fontSize: '26px', fontWeight: '800', margin: '0 0 6px' }}>Payment Complete</h1>
          <p style={{ color: '#888', fontSize: '14px' }}>{r.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} · {r.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>

        <div style={S.card}>
          {/* Customer + Staff */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #1e1e35' }}>
            <div>
              <p style={{ color: '#888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Customer</p>
              <p style={{ color: '#fff', fontSize: '15px', fontWeight: '700' }}>{r.customer.first_name} {r.customer.last_name || ''}</p>
              {r.customer.phone && <p style={{ color: '#666', fontSize: '12px' }}>{r.customer.phone}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              {r.staff && <><p style={{ color: '#888', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Staff</p><p style={{ color: '#fff', fontSize: '14px' }}>{r.staff}</p></>}
              <p style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>{r.paymentMethod.toUpperCase()}{r.id ? ' · #' + r.id : ''}</p>
            </div>
          </div>

          {/* Items */}
          {r.items.map((i, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: idx < r.items.length - 1 ? '1px solid #1e1e3520' : 'none' }}>
              <div>
                <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>{i.name}</span>
                {i.quantity > 1 && <span style={{ color: '#888', fontSize: '12px' }}> ×{i.quantity}</span>}
                {i.type === 'product' && <span style={{ color: '#6c5ce7', fontSize: '10px', marginLeft: '6px' }}>PRODUCT</span>}
                {i.duration && <span style={{ color: '#666', fontSize: '11px', marginLeft: '6px' }}>{i.duration}min</span>}
              </div>
              <span style={{ color: '#00b894', fontWeight: '700', fontSize: '14px' }}>${(i.price * i.quantity).toFixed(2)}</span>
            </div>
          ))}

          {/* Totals */}
          <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #1e1e35' }}>
            <Row label="Subtotal" value={'$' + r.subtotal.toFixed(2)} />
            <Row label="Tax (8.25%)" value={'$' + r.taxAmount.toFixed(2)} />
            {r.tipAmount > 0 && <Row label="Tip" value={'$' + r.tipAmount.toFixed(2)} color="#00b894" />}
            {r.discountAmount > 0 && <Row label="Discount" value={'-$' + r.discountAmount.toFixed(2)} color="#d63031" />}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #1e1e35' }}>
              <span style={{ color: '#fff', fontSize: '20px', fontWeight: '800' }}>Total</span>
              <span style={{ color: '#00b894', fontSize: '28px', fontWeight: '800' }}>${r.total.toFixed(2)}</span>
            </div>
          </div>
          {r.notes && <p style={{ color: '#666', fontSize: '12px', marginTop: '12px', fontStyle: 'italic' }}>Note: {r.notes}</p>}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={printReceipt} style={{ flex: 1, padding: '14px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            🖨️ Print Receipt
          </button>
          <button onClick={resetCheckout} style={{ flex: 1, padding: '14px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            + New Checkout
          </button>
        </div>

        {/* Rebooking prompt */}
        {!showRebook ? (
          <div style={{ marginTop: '12px', background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
            <p style={{ color: '#a78bfa', fontWeight: '700', fontSize: '14px', margin: '0 0 8px' }}>📅 Rebook {r.customer.first_name} for their next visit?</p>
            <p style={{ color: '#666', fontSize: '12px', margin: '0 0 12px' }}>Keep them coming back — schedule their next appointment now</p>
            <button onClick={() => setShowRebook(true)} style={{ padding: '10px 24px', background: '#a78bfa', color: '#000', border: 'none', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', fontSize: '13px' }}>
              Schedule Next Visit →
            </button>
          </div>
        ) : (
          <div style={{ marginTop: '12px', background: '#12121f', border: '1px solid #2a2a45', borderRadius: '12px', padding: '20px' }}>
            <p style={{ color: '#a78bfa', fontWeight: '800', fontSize: '15px', margin: '0 0 14px' }}>📅 Book Next Appointment</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
              <input value={rebookService} onChange={e => setRebookService(e.target.value)}
                placeholder={'Service (e.g. ' + (r.items[0]?.name || 'Massage') + ')'}
                style={{ padding: '9px 12px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <input type="date" value={rebookDate} onChange={e => setRebookDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ flex: 1, padding: '9px 12px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }} />
                <input type="time" value={rebookTime} onChange={e => setRebookTime(e.target.value)}
                  style={{ flex: 1, padding: '9px 12px', background: '#1e1e35', border: '1px solid #2a2a45', borderRadius: '8px', color: '#fff', fontSize: '13px', outline: 'none' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowRebook(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #2a2a45', borderRadius: '8px', color: '#888', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              <button onClick={handleRebook} disabled={rebookSaving || !rebookDate} style={{ flex: 2, padding: '10px', background: '#a78bfa', color: '#000', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', opacity: rebookDate ? 1 : 0.5 }}>
                {rebookSaving ? 'Booking...' : '✅ Confirm Appointment'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════
  // ── CHECKOUT FORM ──
  // ════════════════════════════════════════════
  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 4px' }}>Checkout</h1>
          <p style={{ color: '#888', fontSize: '13px' }}>{bookingId ? '📅 Booking #' + bookingId : 'Quick checkout'}</p>
        </div>
        {items.length > 0 && (
          <div style={{ textAlign: 'right' }}>
            <p style={{ color: '#888', fontSize: '12px' }}>{items.reduce((s, i) => s + i.quantity, 0)} items</p>
            <p style={{ color: '#00b894', fontSize: '22px', fontWeight: '800' }}>${total.toFixed(2)}</p>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '14px', alignItems: 'start' }}>
        {/* ── LEFT: Customer + Items ── */}
        <div>
          {/* Customer */}
          <div style={S.card}>
            <h3 style={S.heading}><span style={{ color: '#ff6b2b' }}>1</span> Customer</h3>
            {!showNewCustomer ? (
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <input ref={custSearchRef} value={customerSearch}
                    onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); if (!e.target.value) { setSelectedCustomer(null); setCustomerBookings([]); } }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                    onKeyDown={handleCustKeyDown}
                    placeholder="Search by name, phone, or email..."
                    style={{ ...S.input, borderColor: selectedCustomer ? '#00b894' : '#1e1e35', paddingLeft: selectedCustomer ? '40px' : '14px' }} />
                  {selectedCustomer && (
                    <>
                      <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '24px', height: '24px', borderRadius: '50%', background: '#ff6b2b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '800' }}>
                        {(selectedCustomer.first_name || '?')[0].toUpperCase()}
                      </span>
                      <button onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); setCustomerBookings([]); custSearchRef.current?.focus(); }}
                        style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', fontSize: '16px', padding: '4px' }}>✕</button>
                    </>
                  )}
                </div>

                {showCustomerDropdown && !selectedCustomer && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#12121f', border: '1px solid #2a2a45', borderRadius: '12px', maxHeight: '320px', overflowY: 'auto', zIndex: 50, marginTop: '4px', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
                    {/* Walk-in option */}
                    <div onClick={() => {
                        var walkIn = { id: 'walk-in', first_name: 'Walk-in', last_name: 'Customer', phone: '', email: '' };
                        selectCustomerAndFetch(walkIn);
                      }}
                      style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1e1e35', display: 'flex', alignItems: 'center', gap: '10px', background: highlightIdx === -1 ? 'transparent' : 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#252840'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#1e1e35', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🚶</span>
                      <div>
                        <div style={{ color: '#aaa', fontWeight: '600', fontSize: '13px' }}>Walk-in Customer</div>
                        <div style={{ color: '#555', fontSize: '11px' }}>No customer record needed</div>
                      </div>
                    </div>

                    {/* Section label */}
                    {!customerSearch.trim() && filteredCustomers.length > 0 && (
                      <div style={{ padding: '8px 14px 4px', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '1px' }}>Recent Customers</div>
                    )}
                    {customerSearch.trim() && filteredCustomers.length > 0 && (
                      <div style={{ padding: '8px 14px 4px', fontSize: '10px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '1px' }}>Search Results</div>
                    )}

                    {filteredCustomers.map((c, idx) => {
                      var initials = ((c.first_name || '?')[0] + (c.last_name || '')[0]).toUpperCase();
                      var isHighlighted = idx === highlightIdx;
                      var colors = ['#ff6b2b', '#6c5ce7', '#00b894', '#0984e3', '#d63031', '#e84393', '#fdcb6e'];
                      var avatarColor = colors[c.id % colors.length] || '#ff6b2b';

                      return (
                        <div key={c.id}
                          onClick={() => selectCustomerAndFetch(c)}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #1e1e3520', display: 'flex', alignItems: 'center', gap: '10px', background: isHighlighted ? '#1e1e35' : 'transparent', transition: 'background 0.1s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#252840'; setHighlightIdx(idx); }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                          {/* Avatar */}
                          <span style={{ width: '36px', height: '36px', borderRadius: '50%', background: avatarColor + '20', color: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '800', flexShrink: 0 }}>
                            {initials}
                          </span>
                          {/* Info */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#fff', fontWeight: '600', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.first_name} {c.last_name || ''}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {c.phone && <span style={{ color: '#666', fontSize: '11px' }}>📱 {c.phone}</span>}
                              {c.email && <span style={{ color: '#555', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✉ {c.email}</span>}
                            </div>
                          </div>
                          {/* Visit count or last visit */}
                          {c.total_bookings > 0 && (
                            <span style={{ color: '#555', fontSize: '10px', flexShrink: 0, background: '#1e1e35', padding: '2px 8px', borderRadius: '10px' }}>
                              {c.total_bookings} visit{c.total_bookings !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {filteredCustomers.length === 0 && customerSearch.trim() && (
                      <div style={{ padding: '14px', textAlign: 'center' }}>
                        <div style={{ color: '#666', fontSize: '13px', marginBottom: '8px' }}>No matches for "{customerSearch}"</div>
                        <button onClick={() => { setShowNewCustomer(true); setNewCust({ ...newCust, first_name: customerSearch.split(' ')[0] || '', last_name: customerSearch.split(' ').slice(1).join(' ') || '' }); }}
                          style={{ background: 'none', border: '1px solid #ff6b2b', borderRadius: '8px', color: '#ff6b2b', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
                          + Create "{customerSearch}"
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => setShowNewCustomer(true)} style={{ marginTop: '8px', background: 'none', border: 'none', color: '#ff6b2b', fontSize: '12px', fontWeight: '600', cursor: 'pointer', padding: 0 }}>
                  + New Customer
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input placeholder="First Name *" value={newCust.first_name} onChange={e => setNewCust({ ...newCust, first_name: e.target.value })} style={S.input} autoFocus />
                  <input placeholder="Last Name" value={newCust.last_name} onChange={e => setNewCust({ ...newCust, last_name: e.target.value })} style={S.input} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <input placeholder="Phone *" value={newCust.phone} onChange={e => setNewCust({ ...newCust, phone: e.target.value })} style={S.input} />
                  <input placeholder="Email" value={newCust.email} onChange={e => setNewCust({ ...newCust, email: e.target.value })} style={S.input} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setShowNewCustomer(false)} style={{ flex: 1, padding: '10px', background: '#1e1e35', border: 'none', borderRadius: '10px', color: '#aaa', fontWeight: '600', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                  <button onClick={handleAddCustomer} disabled={addingCust} style={{ flex: 1, padding: '10px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: '700', cursor: 'pointer', fontSize: '13px', opacity: addingCust ? 0.5 : 1 }}>
                    {addingCust ? 'Adding...' : 'Add & Select'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Linked Appointments */}
          {selectedCustomer && customerBookings.length > 0 && (
            <div style={{ ...S.card, border: '1px solid #ff6b2b30', background: '#ff6b2b08' }}>
              <h3 style={{ ...S.heading, fontSize: '13px' }}>📅 Upcoming Appointments</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {customerBookings.map(bk => {
                  var svc = bk.services || {};
                  var staff = bk.staff || {};
                  var dateStr = bk.booking_date ? new Date(bk.booking_date.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
                  var alreadyInCart = items.find(i => i.id === 'svc-' + (svc.id || bk.service_id));
                  return (
                    <div key={bk.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: alreadyInCart ? '#00b89415' : '#1a1c2e', border: '1px solid ' + (alreadyInCart ? '#00b89440' : '#1e1e35'), borderRadius: '10px' }}>
                      <div>
                        <div style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{svc.name || 'Service'}</div>
                        <div style={{ color: '#666', fontSize: '11px' }}>
                          {dateStr}{bk.start_time ? ' at ' + bk.start_time.slice(0, 5) : ''}
                          {staff.first_name ? ' · ' + staff.first_name : ''}
                          {bk.status === 'pending' ? ' · ⏳ Pending' : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#00b894', fontWeight: '700', fontSize: '14px' }}>${parseFloat(svc.price || bk.final_price || 0).toFixed(2)}</span>
                        {alreadyInCart ? (
                          <span style={{ color: '#00b894', fontSize: '12px', fontWeight: '700' }}>✓ Added</span>
                        ) : (
                          <button onClick={() => {
                            addItem({ id: svc.id || bk.service_id, name: svc.name || 'Service', price: parseFloat(svc.price || bk.final_price || 0), duration: svc.duration }, 'service');
                            if (bk.staff_id && !selectedStaff) setSelectedStaff(String(bk.staff_id));
                            if (!bookingId) setBookingId(bk.id);
                          }} style={{ padding: '5px 12px', background: 'linear-gradient(135deg, #ff6b2b, #ffab40)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '700', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}>
                            + Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Staff */}
          <div style={S.card}>
            <h3 style={S.heading}><span style={{ color: '#ff6b2b' }}>2</span> Staff</h3>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button onClick={() => setSelectedStaff('')} style={S.btn(!selectedStaff)}>None</button>
              {staffList.map(s => (
                <button key={s.id} onClick={() => setSelectedStaff(String(s.id))} style={S.btn(selectedStaff === String(s.id))}>
                  {s.first_name} {(s.last_name || '').charAt(0)}.
                </button>
              ))}
            </div>
          </div>

          {/* Items */}
          <div style={S.card}>
            <h3 style={S.heading}><span style={{ color: '#ff6b2b' }}>3</span> Items</h3>
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              <button onClick={() => { setItemTab('services'); setItemSearch(''); }} style={S.pill(itemTab === 'services')}>
                🛠️ Services {services.length > 0 && <span style={{ opacity: 0.6, marginLeft: 4 }}>({services.length})</span>}
              </button>
              <button onClick={() => { setItemTab('products'); setItemSearch(''); }} style={S.pill(itemTab === 'products')}>
                {isSpaCheckout ? '🧴' : '🏍️'} {isSpaCheckout ? 'Products & Retail' : 'Parts & Inventory'} {products.length > 0 && <span style={{ opacity: 0.6, marginLeft: 4 }}>({products.length})</span>}
              </button>
            </div>

            {/* Category quick-filters for products */}
            {itemTab === 'products' && (() => {
              var cats = [...new Set(products.map(p => p.category).filter(Boolean))];
              if (cats.length === 0) return null;
              return (
                <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => setItemSearch('')}
                    style={{ padding: '4px 10px', borderRadius: '20px', border: '1px solid ' + (!itemSearch ? '#ff6b2b' : '#1e1e35'), background: !itemSearch ? '#ff6b2b15' : 'transparent', color: !itemSearch ? '#ff6b2b' : '#888', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                    All
                  </button>
                  {cats.map(cat => (
                    <button key={cat} onClick={() => setItemSearch(cat)}
                      style={{ padding: '4px 10px', borderRadius: '20px', border: '1px solid ' + (itemSearch === cat ? '#ff6b2b' : '#1e1e35'), background: itemSearch === cat ? '#ff6b2b15' : 'transparent', color: itemSearch === cat ? '#ff6b2b' : '#888', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>
                      {cat}
                    </button>
                  ))}
                </div>
              );
            })()}

            <input placeholder={itemTab === 'services' ? (isSpaCheckout ? 'Search treatments...' : 'Search services...') : (isSpaCheckout ? 'Search products...' : 'Search parts, SKU, category...')} value={itemSearch} onChange={e => setItemSearch(e.target.value)}
              style={{ ...S.input, marginBottom: '10px' }} />
            <div style={{ maxHeight: '240px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {filteredItems.map(item => {
                var price = parseFloat(itemTab === 'products' ? item.retail_price : item.price) || 0;
                var inCart = items.find(i => i.id === (itemTab === 'products' ? 'product' : 'service') + '-' + item.id);
                var lowStock = itemTab === 'products' && item.current_stock !== undefined && item.current_stock <= (item.reorder_level || 5);
                var outOfStock = itemTab === 'products' && item.current_stock !== undefined && item.current_stock <= 0;
                return (
                  <div key={item.id} onClick={() => !outOfStock && addItem(item, itemTab === 'services' ? 'service' : 'product')}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: inCart ? '#ff6b2b10' : outOfStock ? '#1e1e3540' : '#1a1c2e', border: '1px solid ' + (inCart ? '#ff6b2b30' : '#1e1e35'), borderRadius: '10px', cursor: outOfStock ? 'not-allowed' : 'pointer', opacity: outOfStock ? 0.5 : 1 }}
                    onMouseEnter={e => { if (!inCart && !outOfStock) e.currentTarget.style.borderColor = '#ff6b2b40'; }}
                    onMouseLeave={e => { if (!inCart && !outOfStock) e.currentTarget.style.borderColor = '#1e1e35'; }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ color: '#fff', fontWeight: '600', fontSize: '13px' }}>{item.name}</span>
                        {itemTab === 'products' && (
                          <span style={{ background: '#6c5ce720', color: '#6c5ce7', fontSize: '9px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px' }}>RETAIL</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: '#666', marginTop: '2px' }}>
                        {item.category && <span>{item.category}</span>}
                        {item.duration && <span>{item.duration} min</span>}
                        {item.sku && <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>{item.sku}</span>}
                        {itemTab === 'products' && item.current_stock !== undefined && (
                          <span style={{ color: outOfStock ? '#d63031' : lowStock ? '#fdcb6e' : '#00b894' }}>
                            {outOfStock ? '⊘ Out of stock' : lowStock ? '⚠ Low: ' + item.current_stock : '✓ ' + item.current_stock + ' in stock'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <span style={{ color: '#00b894', fontWeight: '700', fontSize: '14px' }}>${price.toFixed(2)}</span>
                      {inCart && <span style={{ background: '#ff6b2b', color: '#fff', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}>{inCart.quantity}</span>}
                    </div>
                  </div>
                );
              })}
              {filteredItems.length === 0 && <p style={{ color: '#555', fontSize: '13px', textAlign: 'center', padding: '16px' }}>No {itemTab === 'products' ? 'parts' : 'services'} found</p>}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Cart + Payment ── */}
        <div style={{ position: 'sticky', top: '20px' }}>
          {/* Cart */}
          <div style={S.card}>
            <h3 style={S.heading}>🛒 Cart</h3>
            {items.length === 0 ? (
              <p style={{ color: '#555', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No items added</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {items.map(item => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #1e1e3520' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>{item.name}</span>
                      </div>
                      <span style={{ fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px',
                        background: item.type === 'product' ? '#6c5ce720' : '#00b89420',
                        color: item.type === 'product' ? '#6c5ce7' : '#00b894',
                      }}>
                        {item.type === 'product' ? 'RETAIL' : 'SERVICE'}
                      </span>
                      {item.duration && <span style={{ fontSize: '10px', color: '#666', marginLeft: '6px' }}>{item.duration} min</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button onClick={() => updateQty(item.id, item.quantity - 1)} style={{ width: '24px', height: '24px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#aaa', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                      <span style={{ color: '#fff', fontWeight: '700', fontSize: '13px', minWidth: '16px', textAlign: 'center' }}>{item.quantity}</span>
                      <button onClick={() => updateQty(item.id, item.quantity + 1)} style={{ width: '24px', height: '24px', background: '#1e1e35', border: 'none', borderRadius: '6px', color: '#aaa', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                      <span style={{ color: '#00b894', fontWeight: '700', fontSize: '13px', minWidth: '56px', textAlign: 'right' }}>${(item.price * item.quantity).toFixed(2)}</span>
                      <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: '#d63031', cursor: 'pointer', fontSize: '14px', padding: '2px' }}>✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tip */}
          <div style={S.card}>
            <h3 style={S.heading}>💰 Tip</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '8px' }}>
              {TIP_PRESETS.map(pct => (
                <button key={pct} onClick={() => { setTipMode('preset'); setTipPreset(tipPreset === pct ? null : pct); setTipCustom(''); }}
                  style={{ ...S.btn(tipMode === 'preset' && tipPreset === pct), padding: '8px 4px', fontSize: '12px' }}>
                  {pct}%
                  {subtotal > 0 && <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>${(subtotal * pct / 100).toFixed(0)}</div>}
                </button>
              ))}
            </div>
            <input type="number" step="0.01" placeholder="Custom tip $" value={tipCustom}
              onChange={e => { setTipCustom(e.target.value); setTipMode('custom'); setTipPreset(null); }}
              style={{ ...S.input, fontSize: '13px', padding: '8px 12px' }} />
          </div>

          {/* Discount */}
          <div style={S.card}>
            <h3 style={S.heading}>🏷️ Discount</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ display: 'flex', gap: '2px', background: '#1a1c2e', borderRadius: '8px', padding: '3px' }}>
                <button onClick={() => setDiscountMode('$')} style={S.pill(discountMode === '$')}>$</button>
                <button onClick={() => setDiscountMode('%')} style={S.pill(discountMode === '%')}>%</button>
              </div>
              <input type="number" step="0.01" placeholder={discountMode === '%' ? 'Percent off' : 'Dollar amount'}
                value={discountValue} onChange={e => setDiscountValue(e.target.value)}
                style={{ ...S.input, flex: 1, fontSize: '13px', padding: '8px 12px' }} />
            </div>
            {discountAmount > 0 && <p style={{ color: '#d63031', fontSize: '12px', marginTop: '6px' }}>−${discountAmount.toFixed(2)} off</p>}
          </div>

          {/* Payment Method */}
          <div style={S.card}>
            <h3 style={S.heading}>💳 Payment</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
              {PAYMENT_METHODS.map(m => (
                <button key={m.key} onClick={() => setPaymentMethod(m.key)} style={{ ...S.btn(paymentMethod === m.key), padding: '10px 6px' }}>
                  <div style={{ fontSize: '18px' }}>{m.icon}</div>
                  <div style={{ fontSize: '11px', marginTop: '2px' }}>{m.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Gift Card */}
          {paymentMethod === 'gift_card' && (
            <div style={S.card}>
              <h3 style={S.heading}>🎁 Gift Card</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={giftCardCode} onChange={e => setGiftCardCode(e.target.value.toUpperCase())}
                  placeholder="Enter gift card code..." style={{ ...S.input, flex: 1, fontSize: 13 }} />
                <button onClick={async () => {
                  try {
                    const r = await axios.get('/api/gift-cards/lookup/' + giftCardCode);
                    if (r.data.success) setGiftCardBalance(r.data.card);
                    else alert('Card not found');
                  } catch(e) { alert('Card not found'); }
                }} style={{ padding: '8px 16px', background: '#1e1e35', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, whiteSpace: 'nowrap' }}>Check</button>
              </div>
              {giftCardBalance && (
                <div style={{ background: '#1a1c2e', borderRadius: 8, padding: '10px 14px', display: 'flex', justify: 'space-between', alignItems: 'center', gap: 12 }}>
                  <span style={{ color: '#888', fontSize: 13 }}>Balance: <strong style={{ color: '#00b894' }}>${parseFloat(giftCardBalance.balance || 0).toFixed(2)}</strong></span>
                  <button onClick={() => setGiftCardApplied(Math.min(parseFloat(giftCardBalance.balance || 0), total))}
                    style={{ padding: '6px 14px', background: '#00b894', color: '#000', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>Apply</button>
                </div>
              )}
              {giftCardApplied > 0 && <p style={{ color: '#00b894', fontSize: 12, marginTop: 6 }}>✅ ${giftCardApplied.toFixed(2)} applied from gift card</p>}
            </div>
          )}
          {/* Notes */}
          <div style={S.card}>
            <textarea placeholder="Notes (optional)..." value={notes} onChange={e => setNotes(e.target.value)}
              rows="2" style={{ ...S.input, resize: 'vertical', fontSize: '13px', padding: '10px 12px' }} />
          </div>

          {/* Summary + Pay */}
          <div style={{ ...S.card, background: '#1a1c2e' }}>
            <Row label="Subtotal" value={'$' + subtotal.toFixed(2)} />
            <Row label="Tax (8.25%)" value={'$' + taxAmount.toFixed(2)} />
            {tipAmount > 0 && <Row label={'Tip' + (tipMode === 'preset' && tipPreset ? ' (' + tipPreset + '%)' : '')} value={'$' + tipAmount.toFixed(2)} color="#00b894" />}
            {discountAmount > 0 && <Row label={'Discount' + (discountMode === '%' ? ' (' + discountValue + '%)' : '')} value={'-$' + discountAmount.toFixed(2)} color="#d63031" />}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #1e1e35' }}>
              <span style={{ color: '#fff', fontSize: '18px', fontWeight: '800' }}>Total</span>
              <span style={{ color: '#00b894', fontSize: '26px', fontWeight: '800' }}>${total.toFixed(2)}</span>
            </div>
          </div>

          <button onClick={handleCheckout} disabled={processing || items.length === 0 || !selectedCustomer}
            style={{
              width: '100%', padding: '16px', borderRadius: '14px', border: 'none', fontSize: '16px', fontWeight: '800', cursor: 'pointer',
              background: items.length > 0 && selectedCustomer ? 'linear-gradient(135deg, #00b894, #00cec9)' : '#1e1e35',
              color: items.length > 0 && selectedCustomer ? '#fff' : '#555',
              opacity: processing ? 0.6 : 1,
            }}>
            {processing ? '⏳ Processing...' : '✅ Pay $' + total.toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: '#888', fontSize: '13px' }}>{label}</span>
      <span style={{ color: color || '#aaa', fontSize: '13px', fontWeight: '600' }}>{value}</span>
    </div>
  );
}

export default Checkout;
